import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'src/@/components/ui/dialog';
import { SETORES_COMPONENT_DESIGN as D } from './SetoresComponent.design';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import useAuth from 'src/hooks/useAuth';
import v1_actionListaSetores from 'src/actions/v1/clientes/v1_actionListaSetores';
import v2_actionCriarSetor from 'src/actions/v2/cliente/setores/v2_actionCriarSetor';
import v2_actionEditarSetor from 'src/actions/v2/cliente/setores/v2_actionEditarSetor';
import v2_actionDeletarSetor from 'src/actions/v2/cliente/setores/v2_actionDeletarSetor';
import Iconify from 'src/components/Iconify';

export default function SetoresComponent() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [listaSetores, setListaSetores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [action, setAction] = useState('Criar');
  const [setorId, setSetorId] = useState('');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    buscarListaSetores();
  }, []);

  async function buscarListaSetores() {
    try {
      const response = await v1_actionListaSetores(user.clienteId);
      if (response.status === 200) {
        setListaSetores(response.data.data);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Erro ao buscar setores', { variant: 'error' });
    }
  }

  const handleOpenDialog = (actionType = 'Criar', setor = null) => {
    setAction(actionType);
    if (setor && actionType === 'Editar') {
      setSetorId(setor._id);
      setNome(setor.nome_setor);
      setDescricao(setor.description || '');
    } else {
      setSetorId('');
      setNome('');
      setDescricao('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSetorId('');
    setNome('');
    setDescricao('');
  };

  const handleSave = async () => {
    if (!nome) return;

    setLoading(true);
    try {
      const data = {
        scope: {
          name: nome,
          description: descricao
        }
      };

      if (action === 'Criar') {
        await v2_actionCriarSetor(user.clienteId, data);
        enqueueSnackbar('Setor criado com sucesso!', { variant: 'success' });
      } else {
        await v2_actionEditarSetor(setorId, data);
        enqueueSnackbar('Setor atualizado com sucesso!', { variant: 'success' });
      }

      await buscarListaSetores();
      handleCloseDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao salvar setor', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSetorId(id);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const data = { checkUsers: true };
      const response = await v2_actionDeletarSetor(setorId, data);

      if (response.status === 200) {
        if (response.data.error) {
          enqueueSnackbar(response.data.message, { variant: 'error' });
        } else {
          enqueueSnackbar('Setor excluído com sucesso!', { variant: 'success' });
          await buscarListaSetores();
        }
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao excluir setor', { variant: 'error' });
    } finally {
      setLoading(false);
      setOpenConfirmDelete(false);
      setSetorId('');
    }
  };

  return (
    <div className={D.root} style={{ fontFamily: D.fontStack }}>
      <div className={D.stickyHeader}>
        <div className={D.container}>
          <div className={D.topbar}>
            <div className={D.topbarIdentity}>
              <div className="min-w-0">
                <h1 className={D.title}>Setores</h1>
                <p className={D.subtitle}>
                  Agrupe atendimentos e pessoas por departamento.
                </p>
              </div>
            </div>
            <div className={D.topbarActions}>
              <button type="button" onClick={() => handleOpenDialog('Criar')} className={D.primaryButton}>
                Novo setor
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`${D.container} pt-6 ${D.pageBody}`}>
        <div className={D.stack}>
          <div className={D.sectionHeader}>
            <div className={D.sectionHeaderMain}>
              <h2 className={D.sectionHeaderTitle}>Lista</h2>
              <p className={D.sectionHeaderSub}>
                Quem atende em cada setor vê só as conversas daquele departamento.
              </p>
            </div>
          </div>

          <div className={D.cardFlush}>
            {listaSetores.length === 0 ? (
              <div className={D.emptyWrap}>
                <div className={D.emptyIcon}>
                  <Iconify icon="eva:layers-outline" width={28} />
                </div>
                <p className={D.emptyTitle}>Nenhum setor encontrado</p>
                <p className={D.emptyDesc}>
                  Crie setores para organizar atendimentos e usuários.
                </p>
                <button type="button" onClick={() => handleOpenDialog('Criar')} className={`${D.link} mt-4`}>
                  Criar meu primeiro setor
                </button>
              </div>
            ) : (
              <div className={D.tableWrap}>
                <div className={D.tableHeadRow}>
                  <div className={`flex-1 min-w-[180px] pr-4 ${D.tableHead}`}>Nome</div>
                  <div className={`flex-1 hidden md:block pr-4 ${D.tableHead}`}>Descrição</div>
                  <div className={`w-24 text-right ${D.tableHead}`}>Ações</div>
                </div>
                {listaSetores.map((setor) => (
                  <div key={setor._id} className={D.tableRow}>
                    <div className={`flex-1 min-w-[180px] pr-4 ${D.tableCell} font-semibold truncate`}>
                      {setor.nome_setor}
                    </div>
                    <div className={`flex-1 hidden md:block pr-4 ${D.tableCellMuted} truncate`}>
                      {setor.description || '—'}
                    </div>
                    <div className="w-24 flex justify-end gap-1.5">
                      <button
                        type="button"
                        title="Editar"
                        onClick={() => handleOpenDialog('Editar', setor)}
                        className={D.iconButton}
                      >
                        <Iconify icon="eva:edit-2-outline" width={16} />
                      </button>
                      <button
                        type="button"
                        title="Excluir"
                        onClick={() => handleDeleteClick(setor._id)}
                        className={D.iconButtonDanger}
                      >
                        <Iconify icon="eva:trash-2-outline" width={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={openDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="sm:max-w-[480px] bg-white border border-[#e7e7ec] rounded-[12px] p-[26px]">
          <DialogHeader>
            <DialogTitle className={D.dialogTitle}>
              {action === 'Criar' ? 'Novo setor' : 'Editar setor'}
            </DialogTitle>
            <DialogDescription className={D.dialogDesc}>
              O nome aparece para a equipe na distribuição de conversas.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-5 py-2">
            <div className={D.field}>
              <label className={D.label}>Nome</label>
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Comercial, Suporte"
                className={D.input}
              />
              <p className={D.hint}>Como o departamento aparece na fila de atendimento.</p>
            </div>
            <div className={D.field}>
              <label className={D.label}>
                Descrição <span className="font-medium text-[#a1a1aa]">(opcional)</span>
              </label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Quando usar este setor"
                rows={3}
                className={D.textarea}
              />
              <p className={D.hint}>Ajuda a equipe a escolher o setor certo.</p>
            </div>
          </div>
          <DialogFooter className={D.dialogFooter}>
            <button type="button" onClick={handleCloseDialog} className={D.ghostButton}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!nome || loading}
              className={D.primaryButton}
            >
              {action === 'Criar' ? 'Criar' : 'Salvar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ModalConfimacao
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        loadingFinalizar={loading}
        mensagem={'Deseja mesmo excluir esse setor? Usuários vinculados a ele podem ser afetados.'}
      />
    </div>
  );
}
